import React from 'react';
import type { ApplicationHistoryItemProps } from 'application_review/components/shared/types';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { Link, Body } from '@grnhse/seedling/lib/birch/typography';
import NotesIcon from '@grnhse/seedling/lib/birch/icons/notes';
import DefinitelyNotIcon from '@grnhse/seedling/lib/birch/icons/definitely_not';
import CheckIcon from '@grnhse/seedling/lib/birch/icons/check';
import styled from 'styled-components';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import { withLocalePrefix } from 'shared/utils/translation';
import DocumentCard from 'talent_matching/components/shared/document_card';
import { Document } from 'talent_matching/types';

const t = withLocalePrefix('application_review.application_history');

type ApplicationHistoryItemWithAttachments = ApplicationHistoryItemProps & {
  attachments?: Document[];
};

type Props = {
  application: ApplicationHistoryItemWithAttachments | null;
  activeDocument?: Document | null;
  onOpenDocument?: (document: Document) => void;
  canDownloadCandidateData?: boolean;
  isRediscovery?: boolean;
  anonymizeResumes?: boolean;
};

const Bullet = styled.div`
  width: 10px;
  min-width: 10px;
  height: 10px;
  min-height: 10px;
  border-radius: 50%;
  margin-top: ${Tokens.spacing['1']};
  background-color: ${FullPalette.GREY_600};
`;

const Container = styled(Stack)`
  background-color: ${FullPalette.GREY_E25};
`;

const StyledBody = styled(Body)<{ color: string }>`
  && {
    ${(props) => `color: ${props.color};`}
  }
`;

const ApplicationHistory = ({
  application,
  activeDocument,
  onOpenDocument,
  canDownloadCandidateData = false,
  isRediscovery = false,
  anonymizeResumes = false,
}: Props) => {
  if (!application) {
    return null;
  }

  const {
    application_stage_name,
    applied_date,
    name,
    previous_application_status,
    previous_application_status_message,
    recruiter,
    scorecards_url,
    url,
    attachments = [],
  } = application;

  return (
    <Stack direction="row" spacing={2}>
      <Bullet />
      <Stack direction="column" spacing={1} sx={{ width: '100%' }}>
        {anonymizeResumes ? (
          <Body>{name}</Body>
        ) : (
          <Link href={url} target="_blank">
            {name}
          </Link>
        )}

        <Stack spacing={0}>
          {applied_date && (
            <Body>{t('applied', { applied_at: applied_date })}</Body>
          )}
          {recruiter && <Body>{t('recruiter', { recruiter })}</Body>}
          {application_stage_name && (
            <Body className="pii-blur">
              {t('stage', { stage: application_stage_name })}
            </Body>
          )}
        </Stack>

        {previous_application_status_message && (
          <Container sx={{ padding: 2 }}>
            <Stack direction="column" spacing={1}>
              {previous_application_status === 'Rejected' && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <DefinitelyNotIcon size="sm" color={FullPalette.POPPY_600} />
                  <StyledBody color={FullPalette.POPPY_600}>
                    {t('rejected')}
                  </StyledBody>
                </Stack>
              )}
              {previous_application_status === 'Hired' && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CheckIcon size="sm" color={FullPalette.GREEN_700} />
                  <StyledBody color={FullPalette.GREEN_700}>
                    {t('hired')}
                  </StyledBody>
                </Stack>
              )}
              <Body>{previous_application_status_message}</Body>
              {scorecards_url && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <NotesIcon
                    size="sm"
                    color={
                      previous_application_status === 'Rejected'
                        ? FullPalette.POPPY_600
                        : FullPalette.GREEN_700
                    }
                  />
                  <Link href={scorecards_url as string} target="_blank">
                    {t('scorecards')}
                  </Link>
                </Stack>
              )}
            </Stack>
          </Container>
        )}

        {attachments.length > 0 && (
          <Stack spacing={1}>
            {attachments.map((doc, index) => (
              <DocumentCard
                key={doc.id ?? index}
                document={doc}
                selected={!!activeDocument && doc.id === activeDocument.id}
                canDownloadCandidateData={canDownloadCandidateData}
                isRediscovery={isRediscovery}
                onClick={
                  doc.previewable !== false && onOpenDocument
                    ? () => onOpenDocument(doc)
                    : undefined
                }
              />
            ))}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default ApplicationHistory;
